import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import {
  Users,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Edit,
  Trash2,
  Lock,
  Unlock,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet, // NUEVO: Para icono del selector
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAdminUsers, useCreateUser, useBulkUploadUsers, useUpdateUser, useDeleteUser } from '../../hooks';
import { adminService } from '../../services/api';
import { Card, Button, Badge, LoadingSpinner, Modal, Alert } from '../../components/common';
import { CreateUserForm, User, UserStatus } from '../../types';
import { useDropzone } from 'react-dropzone';
// ELIMINADO: import { CSVLink } from 'react-csv';
import { exportDataToCSV, exportDataToExcel } from '../../utils/downloadUtils'; // NUEVO
import toast from 'react-hot-toast';
import QuickActions from '@/components/admin/QuickActions';

// Tipo extendido para el formulario de actualización que incluye newPassword
type UpdateUserForm = Partial<User> & {
  newPassword?: string;
};

const AdminUsers: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [zoneFilter, setZoneFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('excel'); // NUEVO: Estado para formato
  const [isExporting, setIsExporting] = useState(false); // NUEVO: Estado de exportación
  const [showChangePassword, setShowChangePassword] = useState(false); // Estado para mostrar campos de contraseña
  const [showNewPassword, setShowNewPassword] = useState(false); // Estado para mostrar/ocultar contraseña

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: usersData, isLoading, refetch } = useAdminUsers({
    page,
    limit: 10,
    search: debouncedSearchQuery,
    status: statusFilter,
    zone: zoneFilter
  });

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const bulkUploadMutation = useBulkUploadUsers();

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    formState: { errors: errorsCreate },
    reset: resetCreate
  } = useForm<CreateUserForm>();

  const {
    register: registerUpdate,
    handleSubmit: handleSubmitUpdate,
    formState: { errors: errorsUpdate },
    reset: resetUpdate,
    setValue: setValueUpdate
  } = useForm<UpdateUserForm>();

  const users = usersData?.users || [];
  const pagination = usersData?.pagination;

  // Configuración de dropzone para carga masiva
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      bulkUploadMutation.mutate(file, {
        onSuccess: () => {
          setShowBulkUploadModal(false);
          refetch();
        }
      });
    } else {
      toast.error('Por favor selecciona un archivo CSV válido');
    }
  }, [bulkUploadMutation, refetch]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    },
    multiple: false
  });

  const onCreateUser = async (data: CreateUserForm) => {
    createUserMutation.mutate(data, {
      onSuccess: () => {
        setShowCreateModal(false);
        resetCreate();
        refetch();
      }
    });
  };

  const onUpdateUser = async (data: UpdateUserForm) => {
    if (!selectedUser) return;

    // Preparar datos para actualización
    const updateData: any = { ...data };

    // Si se está cambiando la contraseña, renombrar el campo a 'password'
    if (showChangePassword && updateData.newPassword) {
      updateData.password = updateData.newPassword;
      delete updateData.newPassword;
    } else {
      // Si no se está cambiando la contraseña, eliminar el campo
      delete updateData.newPassword;
    }

    // console.log('Datos a enviar al backend:', updateData); // Debug

    updateUserMutation.mutate(
      { userId: selectedUser.id, data: updateData },
      {
        onSuccess: () => {
          setShowUserModal(false);
          setSelectedUser(null);
          resetUpdate();
          setShowChangePassword(false);
          setShowNewPassword(false);
          refetch();
        }
      }
    );
  };

  const onDeleteUser = async () => {
    if (!selectedUser) return;

    deleteUserMutation.mutate(selectedUser.id, {
      onSuccess: () => {
        setShowDeleteModal(false);
        setSelectedUser(null);
        refetch();
      }
    });
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setValueUpdate('name', user.name);
    setValueUpdate('email', user.email || '');
    setValueUpdate('geographicZone', user.geographicZone || '');
    setValueUpdate('role', user.role);
    setValueUpdate('status', user.status);
    setShowChangePassword(false); // Resetear estado de cambio de contraseña
    setShowNewPassword(false); // Resetear visibilidad de contraseña
    setShowUserModal(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const toggleUserStatus = async (user: User) => {
    const newStatus = user.status === UserStatus.ACTIVE ? UserStatus.INACTIVE : UserStatus.ACTIVE;
    updateUserMutation.mutate(
      { userId: user.id, data: { status: newStatus } },
      {
        onSuccess: () => {
          refetch();
          toast.success(`Usuario ${newStatus === UserStatus.ACTIVE ? 'activado' : 'desactivado'} exitosamente`);
        }
      }
    );
  };

  const getUserStatusColor = (status: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVE:
        return 'success';
      case UserStatus.INACTIVE:
        return 'warning';
      case UserStatus.SUSPENDED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getUserStatusText = (status: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVE:
        return 'Activo';
      case UserStatus.INACTIVE:
        return 'Inactivo';
      case UserStatus.SUSPENDED:
        return 'Suspendido';
      default:
        return status;
    }
  };

  // NUEVO: Función para manejar exportación con el formato seleccionado
  const handleExportUsers = async () => {
    try {
      setIsExporting(true);

      // Traer TODOS los usuarios que cumplen los filtros activos, no solo la página visible
      const total = pagination?.total ?? 0;
      const { users: allUsers } = await adminService.getUsers({
        page: 1,
        limit: Math.max(total, 1),
        search: debouncedSearchQuery,
        status: statusFilter,
        zone: zoneFilter
      });

      // Preparar datos para exportación
      const formattedUsers = (allUsers || []).map(user => ({
        'ID': user.id,
        'Cédula': user.cedula,
        'Nombre': user.name,
        'Email': user.email || '',
        'Distribuidora': user.distribuidora || '',
        'Zona Geográfica': user.geographicZone || '',
        'Rol': user.role,
        'Estado': getUserStatusText(user.status),
        'Fecha de Registro': new Date(user.createdAt).toLocaleString('es-ES'),
        'Último Acceso': user.lastLogin ? new Date(user.lastLogin).toLocaleString('es-ES') : 'Nunca'
      }));

      if (formattedUsers.length === 0) {
        toast.error('No hay usuarios para exportar');
        return;
      }

      // Generar nombre de archivo
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const fileName = `usuarios_${timestamp}`;

      // Exportar según formato seleccionado
      if (exportFormat === 'excel') {
        // Exportar a Excel
        const XLSX = await import('xlsx');
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(formattedUsers);

        // Anchos de columna automáticos
        const colWidths = Object.keys(formattedUsers[0] || {}).map(key => {
          const maxLength = Math.max(
            key.length,
            ...formattedUsers.map(row => String(row[key as keyof typeof row] || '').length)
          );
          return { wch: Math.min(maxLength + 2, 50) };
        });
        ws['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
        XLSX.writeFile(wb, `${fileName}.xlsx`);
      } else {
        // Exportar a CSV con punto y coma
        const headers = Object.keys(formattedUsers[0]);
        const csvContent = [
          headers.join(';'),
          ...formattedUsers.map(row =>
            headers.map(header => {
              let value = row[header as keyof typeof row];
              if (value === null || value === undefined) return '';
              value = String(value).replace(/"/g, '""');
              if (String(value).includes(';') || String(value).includes('\n') || String(value).includes('"')) {
                return `"${value}"`;
              }
              return value;
            }).join(';')
          )
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${fileName}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }

      const formatLabel = exportFormat === 'excel' ? 'Excel' : 'CSV';
      toast.success(`Usuarios exportados exitosamente (${formatLabel})`);
    } catch (error) {
      console.error('Error exportando usuarios:', error);
      toast.error('Error al exportar usuarios');
    } finally {
      setIsExporting(false);
    }
  };

  const sampleCsvData = `cedula,name,email,geographicZone,distribuidora,password,role
12345678,Juan Pérez,juan@ejemplo.com,Bogotá,Distribuidora Centro,password123,user
87654321,María García,maria@ejemplo.com,Medellín,Distribuidora Antioquia,password123,user
11223344,Carlos Rojas,,Cali,Distribuidora Valle,password123,user`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-brinsa-azul mb-1 md:mb-2">
              Gestión de Usuarios
            </h1>
            <p className="text-sm md:text-base text-brinsa-azulOscuro/80">
              Administra los usuarios de la plataforma
            </p>
          </div>

          {/* Controles en mobile: columna, en desktop: fila */}
          <div className="flex flex-col sm:flex-row gap-2">
            {/* NUEVO: Selector de formato de exportación */}
            <div className="flex items-center space-x-2 border border-brinsa-celeste/40 rounded-lg px-3 py-2 bg-white">
              <FileSpreadsheet className="w-4 h-4 text-brinsa-azul flex-shrink-0" />
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel')}
                className="text-sm bg-transparent border-none focus:ring-0 focus:outline-none text-brinsa-azul font-medium cursor-pointer"
              >
                <option value="excel">Excel (.xlsx)</option>
                <option value="csv">CSV (.csv)</option>
              </select>
            </div>

            {/* ACTUALIZADO: Botón de exportación */}
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={handleExportUsers}
              disabled={isExporting || users.length === 0}
              className="whitespace-nowrap"
            >
              {isExporting ? 'Exportando...' : 'Exportar'}
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<Upload className="w-4 h-4" />}
              onClick={() => setShowBulkUploadModal(true)}
              className="whitespace-nowrap"
            >
              Carga Masiva
            </Button>

            <Button
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowCreateModal(true)}
              className="whitespace-nowrap"
            >
              Nuevo Usuario
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <Card className="p-4 md:p-6 border border-brinsa-celeste/30">
        {/* En mobile: botón toggle. En desktop siempre visible */}
        <div className="md:hidden mb-3">
          <button
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            className="w-full flex items-center justify-between py-2 text-brinsa-azul font-medium"
          >
            <span className="flex items-center">
              <Filter className="w-4 h-4 mr-2" />
              Filtros
            </span>
            {mobileFiltersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        <div className={`space-y-3 ${mobileFiltersOpen ? 'block' : 'hidden md:block'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brinsa-azulOscuro/60" />
              <input
                type="text"
                placeholder="Buscar por nombre o cédula..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
            >
              <option value="">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
              <option value="suspended">Suspendidos</option>
            </select>

            <select
              value={zoneFilter}
              onChange={(e) => {
                setZoneFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
            >
              <option value="">Todas las zonas</option>
              <option value="Norte">Norte</option>
              <option value="Sur">Sur</option>
              <option value="Centro">Centro</option>
              <option value="Oriente">Oriente</option>
              <option value="Occidente">Occidente</option>
            </select>
          </div>

          {(searchQuery || statusFilter || zoneFilter) && (
            <div className="flex flex-wrap gap-2">
              {searchQuery && (
                <Badge variant="default" size="sm">
                  Búsqueda: {searchQuery}
                  <button
                    onClick={() => setSearchQuery('')}
                    className="ml-2 hover:text-white"
                  >
                    ×
                  </button>
                </Badge>
              )}
              {statusFilter && (
                <Badge variant="default" size="sm">
                  Estado: {statusFilter}
                  <button
                    onClick={() => setStatusFilter('')}
                    className="ml-2 hover:text-white"
                  >
                    ×
                  </button>
                </Badge>
              )}
              {zoneFilter && (
                <Badge variant="default" size="sm">
                  Zona: {zoneFilter}
                  <button
                    onClick={() => setZoneFilter('')}
                    className="ml-2 hover:text-white"
                  >
                    ×
                  </button>
                </Badge>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Users Table */}
      <Card className="border border-brinsa-celeste/30 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-brinsa-azulOscuro/70">
            <Users className="w-12 h-12 mb-4" />
            <p className="text-lg font-medium">No se encontraron usuarios</p>
            <p className="text-sm">Intenta ajustar los filtros o crear un nuevo usuario</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brinsa-celeste/10 border-b border-brinsa-celeste/30">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brinsa-azul uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brinsa-azul uppercase tracking-wider">
                      Distribuidora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brinsa-azul uppercase tracking-wider">
                      Zona
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brinsa-azul uppercase tracking-wider">
                      Rol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brinsa-azul uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brinsa-azul uppercase tracking-wider">
                      Último Acceso
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-brinsa-azul uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-brinsa-celeste/5 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-brinsa-azul rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-medium text-sm">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-brinsa-azul">{user.name}</div>
                            <div className="text-sm text-brinsa-azulOscuro/70">CC: {user.cedula}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-brinsa-azulOscuro/80">
                          {/* {user.email || 'No especificado'} */}
                          {user.distribuidora || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-brinsa-azulOscuro/80">
                          {user.geographicZone || 'No especificado'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={user.role === 'admin' ? 'error' : 'default'} size="sm">
                          {user.role === 'admin' ? 'Admin' : 'Usuario'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getUserStatusColor(user.status)} size="sm">
                          {getUserStatusText(user.status)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-brinsa-azulOscuro/70">
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleDateString('es-ES')
                          : 'Nunca'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => toggleUserStatus(user)}
                            className="text-brinsa-azul hover:text-brinsa-azul/80"
                            title={user.status === UserStatus.ACTIVE ? 'Desactivar' : 'Activar'}
                          >
                            {user.status === UserStatus.ACTIVE ? (
                              <Lock className="w-4 h-4" />
                            ) : (
                              <Unlock className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-brinsa-azul hover:text-brinsa-azul/80"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-800"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-slate-200">
              {users.map((user) => (
                <div key={user.id} className="p-4 hover:bg-brinsa-celeste/5 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center flex-1 min-w-0">
                      <div className="w-10 h-10 bg-brinsa-azul rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium text-sm">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-3 flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-brinsa-azul truncate">{user.name}</h3>
                        <p className="text-xs text-brinsa-azulOscuro/70">CC: {user.cedula}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      <button
                        onClick={() => toggleUserStatus(user)}
                        className="text-brinsa-azul hover:text-brinsa-azul/80 p-1"
                      >
                        {user.status === UserStatus.ACTIVE ? (
                          <Lock className="w-4 h-4" />
                        ) : (
                          <Unlock className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-brinsa-azul hover:text-brinsa-azul/80 p-1"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-brinsa-azulOscuro/70">Email:</span>
                      <span className="text-brinsa-azulOscuro/80 truncate ml-2">{user.email || 'No especificado'}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-brinsa-azulOscuro/70">Zona:</span>
                      <span className="text-brinsa-azulOscuro/80">{user.geographicZone || 'No especificado'}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-brinsa-azulOscuro/70">Último acceso:</span>
                      <span className="text-brinsa-azulOscuro/80">
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('es-ES') : 'Nunca'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 pt-2">
                      <Badge variant={user.role === 'admin' ? 'error' : 'default'} size="sm">
                        {user.role === 'admin' ? 'Admin' : 'Usuario'}
                      </Badge>
                      <Badge variant={getUserStatusColor(user.status)} size="sm">
                        {getUserStatusText(user.status)}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs md:text-sm text-brinsa-azulOscuro/80">
            Mostrando {((page - 1) * 10) + 1} - {Math.min(page * 10, pagination.total)} de {pagination.total} usuarios
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Anterior
            </Button>
            <span className="text-xs md:text-sm text-brinsa-azulOscuro/80">
              Página {page} de {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
              disabled={page === pagination.totalPages}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Modal crear usuario */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Crear Nuevo Usuario"
        size="lg"
      >
        <form onSubmit={handleSubmitCreate(onCreateUser)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Nombre completo *
              </label>
              <input
                {...registerCreate('name', {
                  required: 'El nombre es requerido',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' }
                })}
                type="text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
              {errorsCreate.name && (
                <p className="mt-1 text-sm text-red-600">{errorsCreate.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Cédula *
              </label>
              <input
                {...registerCreate('cedula', {
                  required: 'La cédula es requerida',
                  pattern: {
                    value: /^[0-9]+$/,
                    message: 'Solo números'
                  }
                })}
                type="text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
              {errorsCreate.cedula && (
                <p className="mt-1 text-sm text-red-600">{errorsCreate.cedula.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Email
              </label>
              <input
                {...registerCreate('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email inválido'
                  }
                })}
                type="email"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
              {errorsCreate.email && (
                <p className="mt-1 text-sm text-red-600">{errorsCreate.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Zona geográfica
              </label>
              <input
                {...registerCreate('geographicZone')}
                type="text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Distribuidora *
              </label>
              <input
                {...registerCreate('distribuidora', {
                  required: 'La distribuidora es requerida'
                })}
                type="text"
                placeholder="Ej: Distribuidora Antioquia"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
              {errorsCreate.distribuidora && (
                <p className="mt-1 text-sm text-red-600">{errorsCreate.distribuidora.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Contraseña *
              </label>
              <input
                {...registerCreate('password', {
                  required: 'La contraseña es requerida',
                  minLength: { value: 6, message: 'Mínimo 6 caracteres' }
                })}
                type="password"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
              {errorsCreate.password && (
                <p className="mt-1 text-sm text-red-600">{errorsCreate.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Rol *
              </label>
              <select
                {...registerCreate('role', { required: 'El rol es requerido' })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              >
                <option value="user">Usuario</option>
                <option value="admin">Administrador</option>
              </select>
              {errorsCreate.role && (
                <p className="mt-1 text-sm text-red-600">{errorsCreate.role.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCreateModal(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={createUserMutation.isPending}
            >
              Crear Usuario
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal editar usuario */}
      <Modal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        title={`Editar Usuario: ${selectedUser?.name}`}
        size="lg"
      >
        <form onSubmit={handleSubmitUpdate(onUpdateUser)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Nombre completo *
              </label>
              <input
                {...registerUpdate('name', {
                  required: 'El nombre es requerido',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' }
                })}
                type="text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
              {errorsUpdate.name && (
                <p className="mt-1 text-sm text-red-600">{errorsUpdate.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Email
              </label>
              <input
                {...registerUpdate('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email inválido'
                  }
                })}
                type="email"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
              {errorsUpdate.email && (
                <p className="mt-1 text-sm text-red-600">{errorsUpdate.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Zona geográfica
              </label>
              <input
                {...registerUpdate('geographicZone')}
                type="text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Distribuidora *
              </label>
              <input
                {...registerUpdate('distribuidora', {
                  required: 'La distribuidora es requerida'
                })}
                type="text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              />
              {errorsUpdate.distribuidora && (
                <p className="mt-1 text-sm text-red-600">{errorsUpdate.distribuidora.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Rol
              </label>
              <select
                {...registerUpdate('role')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              >
                <option value="user">Usuario</option>
                <option value="admin">Administrador</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-brinsa-azul mb-2">
                Estado
              </label>
              <select
                {...registerUpdate('status')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
              >
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
                <option value="suspended">Suspendido</option>
              </select>
            </div>

            {/* Sección de cambio de contraseña */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-2">
                <input
                  type="checkbox"
                  id="changePassword"
                  checked={showChangePassword}
                  onChange={(e) => {
                    setShowChangePassword(e.target.checked);
                    if (!e.target.checked) {
                      setValueUpdate('newPassword', '');
                      setShowNewPassword(false);
                    }
                  }}
                  className="h-4 w-4 text-brinsa-azul focus:ring-brinsa-celeste border-slate-300 rounded"
                />
                <label htmlFor="changePassword" className="text-sm font-medium text-brinsa-azul cursor-pointer">
                  Cambiar contraseña
                </label>
              </div>

              {showChangePassword && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-brinsa-azul mb-2">
                    Nueva contraseña *
                  </label>
                  <div className="relative">
                    <input
                      {...registerUpdate('newPassword', {
                        required: showChangePassword ? 'La nueva contraseña es requerida' : false,
                        minLength: {
                          value: 6,
                          message: 'La contraseña debe tener al menos 6 caracteres'
                        }
                      })}
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Mínimo 6 caracteres"
                      className="w-full pr-10 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-celeste focus:border-brinsa-celeste"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-400 hover:text-brinsa-azul transition-colors" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-400 hover:text-brinsa-azul transition-colors" />
                      )}
                    </button>
                  </div>
                  {errorsUpdate.newPassword && (
                    <p className="mt-1 text-sm text-brinsa-rojo">
                      {String(errorsUpdate.newPassword.message)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Cédula:</strong> {selectedUser?.cedula} (no modificable)
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowUserModal(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={updateUserMutation.isPending}
            >
              Guardar Cambios
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal eliminar usuario */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Eliminar Usuario"
        size="md"
      >
        <div className="space-y-4">
          <Alert
            type="warning"
            title="¿Estás seguro?"
            message={`Esta acción eliminará permanentemente al usuario "${selectedUser?.name}" y todos sus datos asociados. Esta acción no se puede deshacer.`}
          />

          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Usuario a eliminar:</strong> {selectedUser?.name} ({selectedUser?.cedula})
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={onDeleteUser}
              isLoading={deleteUserMutation.isPending}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Eliminar Usuario
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal carga masiva */}
      <Modal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        title="Carga Masiva de Usuarios"
        size="lg"
      >
        <div className="space-y-6">
          <Alert
            type="info"
            title="Instrucciones para carga masiva"
            message="Sube un archivo CSV con las columnas: cedula, name, email, geographicZone, distribuidora, password, role. El email es opcional: deja el campo vacío entre comas (no escribas 'null')."
          />

          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive ? 'border-brinsa-celeste bg-brinsa-celeste/10' : 'border-slate-300 hover:border-slate-400'
              }`}
          >
            <input {...getInputProps()} />
            <Upload className="w-12 h-12 text-brinsa-azul/50 mx-auto mb-4" />
            {isDragActive ? (
              <p className="text-brinsa-azul">Suelta el archivo aquí...</p>
            ) : (
              <div>
                <p className="text-brinsa-azulOscuro/80 mb-2">
                  Arrastra un archivo CSV aquí, o haz clic para seleccionar
                </p>
                <p className="text-sm text-brinsa-azulOscuro/70">
                  Peso máximo 10 MB por carga
                </p>
              </div>
            )}
          </div>

          {/* Estado de carga */}
          {bulkUploadMutation.isPending && (
            <div className="text-center">
              <LoadingSpinner size="md" />
              <p className="mt-2 text-sm text-brinsa-azulOscuro/80">
                Procesando archivo CSV...
              </p>
            </div>
          )}

          {/* Archivo de ejemplo */}
          <div>
            <h4 className="font-medium text-brinsa-azul mb-2">Archivo de ejemplo:</h4>
            <pre className="bg-brinsa-celeste/10 border border-brinsa-celeste/30 p-3 rounded-lg text-xs overflow-x-auto text-brinsa-azul">
              {sampleCsvData}
            </pre>
            <div className="mt-2">
              <button
                type="button"
                onClick={() => {
                  // BOM UTF-8 para que Excel en Windows lea tildes/ñ correctamente
                  const blob = new Blob(['\uFEFF' + sampleCsvData], { type: 'text/csv;charset=utf-8;' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'ejemplo_usuarios.csv';
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="text-sm text-brinsa-azul hover:text-brinsa-azul/80 underline"
              >
                Descargar archivo de ejemplo
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowBulkUploadModal(false)}
              disabled={bulkUploadMutation.isPending}
            >
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>
      <QuickActions />
    </div>
  );
};

export default AdminUsers;